import {range} from 'lodash';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../core/src/helpers/internationalization/react';
import {blendColors} from '../../../../core/src/models/helpers/colorHelpers';
import {useUserTheme} from '../../theme/userThemeProvider';
import {StarElement} from './starElement';
import {FillingOptions} from './starWithFilling';

/*
 * Props.
 */

export interface SurveyStarsInputProps {
  setScore(score: number): void;
  isReadonly?: boolean;
  color: string;
  score?: number;
}

/*
 * Style.
 */

const StyledStarContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
`;

/*
 * Component.
 */

export const SurveyStarsInput: FC<SurveyStarsInputProps> = ({setScore, score, color, isReadonly}) => {
  const starsCount = 5;
  const rationalizedScore = Math.min(score ?? 0, starsCount);
  const theme = useUserTheme();
  const [hovered, setHovered] = useState<number | undefined>(undefined);
  const darkenColor = getOrElse(() => blendColors(color, theme.alphas.black50), '#000000');

  const setHoveredStar = (value: number | undefined) => () => {
    if (isReadonly) {
      return;
    }
    setHovered(value);
  };

  const setScoreStar = (value: number) => () => {
    if (isReadonly) {
      return;
    }
    setScore(value);
  };

  const readOnlyLabel = (
    <Plural
      id="starsInput-readonlyLabel"
      value={rationalizedScore}
      _0="Unrated"
      one="Rated # star"
      other="Rated # stars"
    />
  );

  return (
    <StyledStarContainerDiv>
      {range(1, 1 + starsCount).map((value) => {
        const fillColor = ((): FillingOptions => {
          if (hovered && value <= hovered) {
            return {
              type: 'fill',
              color: darkenColor,
            };
          }

          const remaing = rationalizedScore - (value - 1);

          if (remaing >= 0) {
            if (remaing >= 1) {
              return {
                type: 'fill',
                color,
              };
            }

            return {
              type: 'mid',
              fillColor: color,
              emptyColor: theme.greys.shade40,
              breakingPoint: remaing,
            };
          }

          return {
            type: 'fill',
            color: theme.greys.shade40,
          };
        })();

        const label = isReadonly ? (
          readOnlyLabel
        ) : (
          <Trans id="starsInput-label">{`Rate ${value} stars`}</Trans>
        );

        return (
          <StarElement
            isSelected={rationalizedScore - value >= 0}
            onMouseEnter={setHoveredStar(value)}
            onMouseLeave={setHoveredStar(undefined)}
            label={label}
            color={fillColor}
            onClick={setScoreStar(value)}
            key={`star-${value}`}
          />
        );
      })}
    </StyledStarContainerDiv>
  );
};

/*
 * Helpers.
 */

function getOrElse<T>(throwable: () => T, defaultValue: T): T {
  try {
    return throwable();
  } catch (e) {
    return defaultValue;
  }
}
